// src/services/shipmentService.js
// Handles async shipment pipeline: status polling, tracking, manual retry
// Backend uses BullMQ async queue — shipment is NOT immediate after payment.
// See docs/briefs/FRONTEND_INTEGRATION_GUIDE.md for full state machine.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const handleResponse = async (res) => {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error ?? err.message ?? `Request failed: ${res.status}`);
    error.status = res.status;
    error.data = err;
    throw error;
  }
  return res.json();
};

/**
 * GET /api/shipping/order/:orderId
 * Get current shipment status for an order.
 * May return 412 if shipment not created yet — caller must handle gracefully.
 *
 * @param {string} orderId  - e.g. "ORD-1699564800000-ABC12"
 * @param {string} idToken  - Firebase ID token
 * @returns {Object}        - { shipping: { status, awb_code, courier_name, tracking_url, ... } }
 */
export const getShipmentStatus = async (orderId, idToken) => {
  const res = await fetch(`${API_URL}/api/shipping/order/${orderId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });
  // 412 = shipment not yet created (normal async state) — return null to signal pending
  if (res.status === 412) {
    return null;
  }
  // 503 = feature disabled — return null silently per AGENTS.md §6
  if (res.status === 503) {
    return null;
  }
  return handleResponse(res);
};

/**
 * GET /api/shipping/track/:awb
 * Public endpoint — no auth required.
 * Returns full tracking event history for an AWB code.
 *
 * @param {string} awb - AWB tracking code e.g. "1234567890123"
 * @returns {Object}   - { tracking: { status, courier_name, events, estimated_delivery_date, ... } }
 */
export const trackShipment = async (awb) => {
  const res = await fetch(`${API_URL}/api/shipping/track/${awb}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (res.status === 503) return null;
  return handleResponse(res);
};

/**
 * POST /api/shipping/retry/:orderId
 * User-facing manual retry. Only valid for permanently failed orders.
 * Backend responds 202 Accepted (async queue).
 * 409 = shipment already exists (don't show as error).
 * 429 = rate limited — show "please wait" per AGENTS.md §6.
 *
 * @param {string} orderId - Order ID to retry
 * @param {string} idToken - Firebase ID token
 * @returns {Object}       - { success, message, status }
 */
export const retryShipment = async (orderId, idToken) => {
  const res = await fetch(`${API_URL}/api/shipping/retry/${orderId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({}),
  });
  // 409 = shipment already succeeded — not a real error
  if (res.status === 409) {
    const data = await res.json().catch(() => ({}));
    return { success: true, alreadyCreated: true, shipment_id: data.shipment_id };
  }
  if (res.status === 429) {
    const error = new Error('rate_limited');
    error.status = 429;
    throw error;
  }
  if (res.status === 503) {
    return null;
  }
  return handleResponse(res);
};

/**
 * POST /api/admin/shipments/retry/:orderId
 * Admin-only force retry. Requires admin token.
 *
 * @param {string} orderId - Order ID to retry
 * @param {string} reason  - Optional reason string
 * @param {string} idToken - Admin Firebase ID token
 * @returns {Object}       - { success, message, orderId }
 */
export const adminRetryShipment = async (orderId, reason, idToken) => {
  const res = await fetch(`${API_URL}/api/admin/shipments/retry/${orderId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ reason: reason || '' }),
  });
  if (res.status === 503) return null;
  return handleResponse(res);
};

/**
 * POST /api/shipping/options  (API v2.7 — Feature 33)
 * Fetch real-time Shiprocket serviceability rates for a pincode + cart.
 *
 * Return values:
 *   Array of courier objects  → success (1–3 couriers, never empty on 200)
 *   Symbol('no_coverage')     → HTTP 422 — pincode has no supported courier coverage
 *   null                      → 503 / 500 / network / timeout — UI falls back to static options
 *   throws { status: 429 }    → rate limited — caller shows "please wait" message
 *
 * @param {string} pincode   - 6-digit destination pincode
 * @param {Array}  items     - Cart items [ { id, name, price, quantity, ... } ]
 * @param {number} total     - Order total in INR
 * @returns {Array|Symbol|null}
 *
 * Each courier object (v2.7):
 * {
 *   id:           number,   // Shiprocket courier_company_id (pass as shippingMethod)
 *   name:         string,   // e.g. "Blue Dart Express"
 *   price:        number,   // Shipping cost in INR (always > 0 on live; 0 on static fallback)
 *   etdDays:      number,   // Estimated delivery days (numeric)
 *   etd:          string|null, // ShipRocket formatted ETD e.g. "2 Days"
 *   rating:       number,   // Quality rating 0–5
 *   codAvailable: boolean,
 *   isAir:        boolean,  // true = air freight
 *   isRecommended: boolean, // true for at most one courier
 * }
 */
export const NO_COVERAGE = Symbol('no_coverage');

export const getShippingOptions = async (pincode, items, total) => {
  try {
    const res = await fetch(`${API_URL}/api/shipping/options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pincode, items, total }),
      signal: AbortSignal.timeout(8000), // 8 s timeout — fall back to static on slow Shiprocket
    });

    // 503 = feature disabled or Shiprocket down — silently fall back to static
    if (res.status === 503) return null;

    // 429 = rate limited — surface to caller so UI shows "please wait"
    if (res.status === 429) {
      const error = new Error('rate_limited');
      error.status = 429;
      throw error;
    }

    // 422 = pincode not serviceable — UI blocks checkout with clear message
    if (res.status === 422) return NO_COVERAGE;

    // 400 = bad request (shouldn't happen via UI) — fall back silently
    if (!res.ok) return null;

    const data = await res.json();
    // v2.7: { success: true, couriers: [...] }
    return Array.isArray(data.couriers) ? data.couriers : null;
  } catch (err) {
    // Re-throw 429 so the caller can show a message
    if (err.status === 429) throw err;
    // Everything else (network, timeout, JSON parse) → null = use fallback
    return null;
  }
};
